import { apiClient } from '@/lib/api/client';
import type {
  DashboardData,
  ChartDataPoint,
  DashboardMetrics,
  RecentOrder,
  ShopVisitsData,
  ProductSearchesData,
  OrdersData,
  RevenueData,
  ProductsData,
  SummaryData,
} from '../types/dashboard.types';

interface ApiData<T> {
  data: T;
}

interface DashboardMetricsResponse {
  shopVisits: {
    totalVisits: number;
    uniqueVisitors: number;
    growthPercentage: number;
    dailyTrend: Array<{ date: string; value: number }>;
    averageDailyVisits: number;
  };
  productSearches: {
    totalSearchAppearances: number;
    growthPercentage: number;
    topSearchedProducts: Array<{
      productId: string;
      productName: string;
      searchCount: number;
      clickThroughRate: number;
    }>;
    topSearchTerms: Array<{ term: string; count: number }>;
    dailyTrend: Array<{ date: string; value: number }>;
  };
  orders: {
    totalOrders: number;
    activeOrders: number;
    completedOrders: number;
    pendingOrders: number;
    cancelledOrders: number;
    growthPercentage: number;
    dailyTrend: Array<{ date: string; value: number }>;
    completionRate: number;
    cancellationRate: number;
    ordersByStatus: Record<string, number>;
    ordersByPaymentMethod: Record<string, number>;
    ordersByCountry: Record<string, number>;
  };
  revenue: {
    totalRevenue: number;
    currentPeriodRevenue: number;
    previousPeriodRevenue: number;
    growthPercentage: number;
    averageOrderValue: number;
    dailyTrend: Array<{ date: string; value: number | string }>;
    peakRevenue: number;
    peakRevenueDate: string;
    revenueByCountry: Record<string, number>;
  };
  products: {
    totalProducts: number;
    activeProducts: number;
    outOfStockProducts: number;
    lowStockProducts: number;
  };
  summary: {
    totalVisits: number;
    totalOrders: number;
    totalRevenue: number;
    conversionRate: number;
    performanceScore: number;
  };
}

interface WalletBalance {
  available: number;
  pending: number;
  total: number;
}

// Helper to parse chart values (API sometimes returns strings for decimal columns)
function parseChartValue(value: number | string): number {
  if (typeof value === 'string') {
    return parseFloat(value) || 0;
  }
  return value || 0;
}

// Helper to map daily trend to chart data
function mapDailyTrend(trend: Array<{ date: string; value: number | string }> | undefined): ChartDataPoint[] {
  if (!trend) return [];
  return trend.map((item) => ({
    date: item.date,
    value: parseChartValue(item.value),
  }));
}

// Server order structure from /order/list/shops
interface ServerOrder {
  id: string;
  orderId: string;
  orderDate: string;
  status: string;
  paymentStatus: string;
  deliveryType: string;
  customer: string;
  quantity: number;
  shopName: string;
  total_price: {
    euro: number;
    usd: number;
    comoros: number;
  };
}

// Transform server order to frontend RecentOrder
function transformOrder(serverOrder: ServerOrder): RecentOrder {
  return {
    id: serverOrder.id || '',
    refId: serverOrder.orderId || '',
    customerName: serverOrder.customer || '',
    total: serverOrder.total_price?.euro ?? 0,
    status: serverOrder.status || '',
    createdAt: serverOrder.orderDate || new Date().toISOString(),
  };
}

export const dashboardService = {
  async getDashboardData(): Promise<DashboardData> {
    try {
      // Fetch data from multiple endpoints in parallel
      const [statsResponse, balanceResponse, ordersResponse] = await Promise.allSettled([
        apiClient.get<ApiData<DashboardMetricsResponse>>('/metrics/dashboard'),
        apiClient.get<ApiData<WalletBalance>>('/payment/seller/balance'),
        apiClient.get<ApiData<{ data: ServerOrder[]; activePage: number; totalPage: number }>>('/order/list/shops?page=1&limit=5'),
      ]);

      // Extract stats from response.data.data with safe access
      const stats = statsResponse.status === 'fulfilled' ? statsResponse.value.data?.data : null;
      const balance = balanceResponse.status === 'fulfilled' ? balanceResponse.value.data?.data : null;

      // Handle orders response - server returns { message, data: { data: [...], activePage, totalPage } }
      let recentOrders: RecentOrder[] = [];
      if (ordersResponse.status === 'fulfilled') {
        const ordersData = ordersResponse.value.data?.data;
        let rawOrders: ServerOrder[] = [];
        if (Array.isArray(ordersData)) {
          rawOrders = ordersData;
        } else if (ordersData?.data && Array.isArray(ordersData.data)) {
          rawOrders = ordersData.data;
        }
        recentOrders = rawOrders.map(transformOrder);
      }

      // Map the API response to dashboard metrics
      const metrics: DashboardMetrics = {
        todaysSales: stats?.revenue?.currentPeriodRevenue ?? 0,
        totalOrders: stats?.orders?.totalOrders ?? stats?.summary?.totalOrders ?? 0,
        totalRevenue: balance?.total ?? stats?.revenue?.totalRevenue ?? stats?.summary?.totalRevenue ?? 0,
        pendingOrders: stats?.orders?.pendingOrders ?? 0,
        salesTrend: stats?.revenue?.growthPercentage ?? 0,
        ordersTrend: stats?.orders?.growthPercentage ?? 0,
        revenueTrend: stats?.revenue?.growthPercentage ?? 0,
      };

      // Map shop visits data
      const shopVisits: ShopVisitsData = {
        totalVisits: stats?.shopVisits?.totalVisits ?? 0,
        uniqueVisitors: stats?.shopVisits?.uniqueVisitors ?? 0,
        growthPercentage: stats?.shopVisits?.growthPercentage ?? 0,
        dailyTrend: mapDailyTrend(stats?.shopVisits?.dailyTrend),
        averageDailyVisits: stats?.shopVisits?.averageDailyVisits ?? 0,
      };

      // Map product searches data
      const productSearches: ProductSearchesData = {
        totalSearchAppearances: stats?.productSearches?.totalSearchAppearances ?? 0,
        growthPercentage: stats?.productSearches?.growthPercentage ?? 0,
        topSearchedProducts: stats?.productSearches?.topSearchedProducts ?? [],
        topSearchTerms: stats?.productSearches?.topSearchTerms ?? [],
        dailyTrend: mapDailyTrend(stats?.productSearches?.dailyTrend),
      };

      // Map orders data
      const ordersData: OrdersData = {
        totalOrders: stats?.orders?.totalOrders ?? 0,
        activeOrders: stats?.orders?.activeOrders ?? 0,
        completedOrders: stats?.orders?.completedOrders ?? 0,
        pendingOrders: stats?.orders?.pendingOrders ?? 0,
        cancelledOrders: stats?.orders?.cancelledOrders ?? 0,
        growthPercentage: stats?.orders?.growthPercentage ?? 0,
        dailyTrend: mapDailyTrend(stats?.orders?.dailyTrend),
        completionRate: stats?.orders?.completionRate ?? 0,
        cancellationRate: stats?.orders?.cancellationRate ?? 0,
        ordersByStatus: stats?.orders?.ordersByStatus ?? {},
        ordersByPaymentMethod: stats?.orders?.ordersByPaymentMethod ?? {},
        ordersByCountry: stats?.orders?.ordersByCountry ?? {},
      };

      // Map revenue data
      const revenueData: RevenueData = {
        totalRevenue: stats?.revenue?.totalRevenue ?? 0,
        currentPeriodRevenue: stats?.revenue?.currentPeriodRevenue ?? 0,
        previousPeriodRevenue: stats?.revenue?.previousPeriodRevenue ?? 0,
        growthPercentage: stats?.revenue?.growthPercentage ?? 0,
        averageOrderValue: stats?.revenue?.averageOrderValue ?? 0,
        dailyTrend: mapDailyTrend(stats?.revenue?.dailyTrend),
        peakRevenue: stats?.revenue?.peakRevenue ?? 0,
        peakRevenueDate: stats?.revenue?.peakRevenueDate ?? '',
        revenueByCountry: stats?.revenue?.revenueByCountry ?? {},
      };

      // Map products data
      const productsData: ProductsData = {
        totalProducts: stats?.products?.totalProducts ?? 0,
        activeProducts: stats?.products?.activeProducts ?? 0,
        outOfStockProducts: stats?.products?.outOfStockProducts ?? 0,
        lowStockProducts: stats?.products?.lowStockProducts ?? 0,
      };

      // Map summary data
      const summary: SummaryData = {
        totalVisits: stats?.summary?.totalVisits ?? 0,
        totalOrders: stats?.summary?.totalOrders ?? 0,
        totalRevenue: stats?.summary?.totalRevenue ?? 0,
        conversionRate: stats?.summary?.conversionRate ?? 0,
        performanceScore: stats?.summary?.performanceScore ?? 0,
      };

      return {
        metrics,
        salesChart: revenueData.dailyTrend,
        ordersChart: ordersData.dailyTrend,
        recentOrders: recentOrders.slice(0, 5),
        topProducts: [],
        shopVisits,
        productSearches,
        ordersData,
        revenueData,
        productsData,
        summary,
      };
    } catch (error) {
      console.error('[DashboardService] Error fetching dashboard data:', error);
      // Return default data instead of throwing
      const emptyChartData: ChartDataPoint[] = [];
      return {
        metrics: {
          todaysSales: 0,
          totalOrders: 0,
          totalRevenue: 0,
          pendingOrders: 0,
          salesTrend: 0,
          ordersTrend: 0,
          revenueTrend: 0,
        },
        salesChart: emptyChartData,
        ordersChart: emptyChartData,
        recentOrders: [],
        topProducts: [],
        shopVisits: {
          totalVisits: 0,
          uniqueVisitors: 0,
          growthPercentage: 0,
          dailyTrend: emptyChartData,
          averageDailyVisits: 0,
        },
        productSearches: {
          totalSearchAppearances: 0,
          growthPercentage: 0,
          topSearchedProducts: [],
          topSearchTerms: [],
          dailyTrend: emptyChartData,
        },
        ordersData: {
          totalOrders: 0,
          activeOrders: 0,
          completedOrders: 0,
          pendingOrders: 0,
          cancelledOrders: 0,
          growthPercentage: 0,
          dailyTrend: emptyChartData,
          completionRate: 0,
          cancellationRate: 0,
          ordersByStatus: {},
          ordersByPaymentMethod: {},
          ordersByCountry: {},
        },
        revenueData: {
          totalRevenue: 0,
          currentPeriodRevenue: 0,
          previousPeriodRevenue: 0,
          growthPercentage: 0,
          averageOrderValue: 0,
          dailyTrend: emptyChartData,
          peakRevenue: 0,
          peakRevenueDate: '',
          revenueByCountry: {},
        },
        productsData: {
          totalProducts: 0,
          activeProducts: 0,
          outOfStockProducts: 0,
          lowStockProducts: 0,
        },
        summary: {
          totalVisits: 0,
          totalOrders: 0,
          totalRevenue: 0,
          conversionRate: 0,
          performanceScore: 0,
        },
      };
    }
  },

  async getWalletBalance(): Promise<WalletBalance | null> {
    try {
      const response = await apiClient.get<ApiData<WalletBalance>>('/payment/seller/balance');
      return response.data?.data ?? null;
    } catch {
      return null;
    }
  },
};
